import { PageLoadError } from '@components/page-load-error';
import {
  callCobrandErrorToast,
  getCobrandQueryOptions,
} from '@features/navigation/api/get-cobrand';
import { getProfileQueryOptions } from '@features/user/api/get-profile';
import {
  GetProfileResponse,
  UserState,
} from '@features/user/types/get-profile-response';
import Box from '@mui/material/Box';
import { getBetaFeaturesQueryOptions } from '@src/api/beta-features/get-beta-features';
import { setGaUserProperties } from '@src/lib/google-analytics';
import AuthenticatedChromeless from '@src/routes/-layouts/authenticated-chromeless-layout';
import { CobrandImage } from '@src/types/get-cobrand-response';
import { createFileRoute } from '@tanstack/react-router';
import { getCookie, hasAirMaxEnabled } from '@utils';
import { AxiosError } from 'axios';

type AuthenticatedChromelessRouteData = {
  profile: GetProfileResponse;
  cobrandImage?: CobrandImage;
};

export const Route = createFileRoute('/_authenticated-chromeless')({
  component: AuthenticatedChromeless,
  errorComponent: ({ error }) => (
    <Box
      height='100vh'
      sx={(theme) => ({ background: theme.customGradients.primary })}
    >
      <PageLoadError pageName='Max' error={error} />
    </Box>
  ),
  beforeLoad: async ({ context }) => {
    const data = await context.queryClient.fetchQuery(
      getBetaFeaturesQueryOptions,
    );
    void hasAirMaxEnabled(data);
  },
  loader: async ({ context }): Promise<AuthenticatedChromelessRouteData> => {
    const partner = getCookie('partner');
    const [profileResult, cobrandResult] = await Promise.allSettled([
      context.queryClient.fetchQuery(getProfileQueryOptions),
      context.queryClient.fetchQuery(getCobrandQueryOptions({ partner })),
    ]);

    let profile: GetProfileResponse | undefined;
    if (profileResult.status === 'fulfilled') {
      profile = profileResult.value;
      // Rails hands off straight to these pages, so set the GA user properties
      // (businessUser included) here: a loader finishes before the pages
      // render, so they're set before the pages send any event.
      setGaUserProperties(profile);
    } else {
      throw new AxiosError('Failed to load user profile. Please log in again.');
    }

    let cobrandImage: CobrandImage | undefined;
    if (cobrandResult.status === 'fulfilled') {
      cobrandImage = cobrandResult.value?.image;
    } else {
      callCobrandErrorToast();
      console.error(cobrandResult.reason);
    }

    if (profile && profile.state !== UserState.Approved) {
      window.location.replace('/account-under-review');
    }

    return {
      profile,
      cobrandImage,
    };
  },
});
